import { useState, useRef, useEffect } from 'react';
import { convertIsoUTCToLocal } from '../../libs.jsx';
import './notes-cell.css';

/**
 * CallsCell — inline indicator for calls placed during an event (Grandstream
 * campuses). Read-only counterpart to NotesCell:
 *   - calls.length === 0 → muted "—"
 *   - calls.length  >  0 → count badge; hover shows every call's when / duration /
 *                          sender → receiver.
 *
 * Call records mirror the Call Logs report shape:
 *   { call_start_time (UTC), call_length, caller, receiver, call_type_name, event_id }
 *
 * @param {{
 *   calls?:      Array<object>,
 *   tzOffset?:   number,   // for localizing call_start_time
 *   ariaPrefix?: string,   // optional context for aria-label
 * }} props
 */
const PREVIEW_W = 340;

const CallsCell = ({ calls, tzOffset, ariaPrefix }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);

  const list = Array.isArray(calls) ? calls : [];
  const count = list.length;

  // Close on scroll — the fixed popover would otherwise drift from the badge.
  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  if (count === 0) {
    return <span className="nc-disabled" aria-hidden="true">{'—'}</span>;
  }

  const handleEnter = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceRight = window.innerWidth - rect.left;
    setStyle({
      left: spaceRight < PREVIEW_W + 16
        ? Math.max(8, rect.right - PREVIEW_W) + 'px'
        : `${rect.left}px`,
      top: spaceBelow < 220 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 220 ? `${window.innerHeight - rect.top + 6}px` : undefined,
    });
    setOpen(true);
  };

  const handleLeave = () => setOpen(false);

  const label = `${ariaPrefix ? ariaPrefix + ' — ' : ''}${count} call${count !== 1 ? 's' : ''} placed during this event`;

  return (
    <span
      className="nc-cell"
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <span
        className="nc-badge"
        style={{ cursor: 'default' }}
        aria-label={label}
        title={`${count} call${count !== 1 ? 's' : ''}`}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, minWidth: '12px' }}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.74a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92z" />
        </svg>
        {count}
      </span>
      {open ? (
        <span className="nc-popover" style={{ position: 'fixed', ...style }} role="tooltip">
          <span className="nc-popover__title">Calls ({count})</span>
          <ul className="nc-popover__list">
            {list.map((c, i) => (
              <li key={c.call_id || i} className="nc-popover__item">
                <span className="nc-popover__meta">
                  <span>{c.call_start_time ? convertIsoUTCToLocal(c.call_start_time, tzOffset) : '—'}</span>
                  <span>{c.call_length || '—'}</span>
                </span>
                <span className="nc-popover__text">
                  {(c.caller || '—')} {'→'} {(c.receiver || '—')}
                </span>
              </li>
            ))}
          </ul>
        </span>
      ) : null}
    </span>
  );
};

export default CallsCell;
